import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { FiberManualRecord } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";

import MobileTopNavstyle from "@/components/Navigation/Mobile/MobileTopNavigation/index.module.css";
import GetImages from "@/components/GetImages";

import {
  IChangeHospitalModuleName,
  IProfileDropdownEnums,
} from "@/models/doctor";
import { IRoleType } from "@/models/role";
import { IResetAppState, ISessionKeys } from "@/models";
import { IOnboardingSteps } from "@/models/onboarding";

import {
  setChangeHospitalModuleName,
  setTopBarDropdownItems,
} from "@/store/slices/doctor/topNavigation";
import { setChatSearchOn } from "@/store/slices/chat";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setIsPendingOrderRequestsOpen } from "@/store/slices/pharmacyClinicDiagnostic";
import { setStepsTest } from "@/store/slices/patient/tests";
import {
  config,
  setIsShowChangeLocationModal,
  setIsShowNotification,
  setRedirectAfterLogin,
} from "@/store/slices/config";
import { setSteps } from "@/store/slices/onboarding/onboarding";

import {
  clearLocalStorageExcept,
  isClinic,
  topBarDropdownDispatchingEnums,
} from "@/utils";
import { storage } from "@/utils/Storage";

import { deleteToken } from "firebase/messaging";
import firebaseService from "@/firebaseConfig";

const useMobileTopNavigation = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { cartDetails } = useAppSelector((state) => state.tests);
  const { location, isAllNotificationRead, inAppNotification } =
    useAppSelector(config) || {};

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const locationDetails =
    location?.slice(0, 18) + (location?.length > 18 ? "..." : "");

  const isClinicRole = isClinic();

  const chatHandler = async () => {
    if (!roleType) {
      navigate("/");
      return;
    }
    if (roleType !== IRoleType.GUEST) {
      navigate("/chat");
    } else {
      dispatch({ type: IResetAppState.RESET_ALL });
      clearLocalStorageExcept(ISessionKeys.LOCATION);
      dispatch(setSteps(IOnboardingSteps.VERIFY_MOBILE_NUMBER));
      await deleteToken(firebaseService.messaging);
      dispatch(setRedirectAfterLogin({ path: "/chat", isRedirect: true }));
      navigate("/");
    }
  };

  const generateNavItems = (
    selectedHospital: string,
    pendingOrderList: any
  ) => {
    const chatNotificationIcon = (
      <Grid container alignItems="center">
        <Grid item mx={2} onClick={() => dispatch(setIsShowNotification(true))}>
          <GetImages
            name={
              isAllNotificationRead || inAppNotification?.length === 0
                ? "ReadMessageBellPWAIcon"
                : "UnReadMessageBellPWAIcon"
            }
            width="24"
            height="24"
          />
        </Grid>
        <Grid item>
          <Box onClick={chatHandler} sx={{ cursor: "pointer" }}>
            <GetImages name="MessageNavIcon" width="30" height="24" />
          </Box>
        </Grid>
      </Grid>
    );

    const patientOrGuestItem = [
      {
        path: "/home",
        icon: <GetImages name="HomeNavIcon" width="36" height="36" />,
        title:
          storage.getSessionToken("name") ??
          (roleType === IRoleType.GUEST ? "Hi Guest" : "Unknown"),
        locationIcon: true,
        subText: locationDetails,
        subTextHandler: () => dispatch(setIsShowChangeLocationModal(true)),
        secondIcon: chatNotificationIcon,
        isShowSearch: true,
      },
      {
        path: "/find-doctor",
        icon: <GetImages name="DoctorNavIcon" width="36" height="36" />,
        title: intl.formatMessage({
          id: "find_doctors",
          defaultMessage: "Find Doctors",
        }),
        locationIcon: true,
        subText: locationDetails,
        subTextHandler: () => dispatch(setIsShowChangeLocationModal(true)),
        secondIcon: chatNotificationIcon,
        isShowSearch: true,
      },
      {
        path: "/medicines",
        icon: <GetImages name="MedicineNavIcon" width="36" height="36" />,
        title: intl.formatMessage({
          id: "get_medicines",
          defaultMessage: "Get Medicines",
        }),
        locationIcon: true,
        subText: locationDetails,
        subTextHandler: () => dispatch(setIsShowChangeLocationModal(true)),
        secondIcon: null,
        isShowSearch: true,
      },
      {
        path: "/test-reports",
        icon: <GetImages name="TestNavIcon" width="36" height="36" />,
        title: intl.formatMessage({
          id: "book_tests",
          defaultMessage: "Book Tests",
        }),
        locationIcon: true,
        subText: locationDetails,
        subTextHandler: () => dispatch(setIsShowChangeLocationModal(true)),
        secondIcon: (
          <Grid
            item
            sx={{ display: "flex", alignItems: "center" }}
            ml={2}
            onClick={() => {
              dispatch(setStepsTest(3));
            }}
          >
            <GetImages name="CartNavIcon" width="24" height="24" />
            <Typography className={MobileTopNavstyle.redMark}>
              {cartDetails?.tests?.length > 0 && (
                <FiberManualRecord
                  style={{ fontSize: "small", color: "var(--red-400)" }}
                />
              )}
            </Typography>
          </Grid>
        ),
        isShowSearch: true,
      },
      {
        path: "/health-hub",
        icon: <GetImages name="TvNavIcon" width="36" height="36" />,
        title: intl.formatMessage({
          id: "health_hub",
          defaultMessage: "Health Hub",
        }),
        secondIcon: null,
        locationIcon: false,
        subText: "",
        isShowSearch: true,
      },
    ];
    const doctorItem = [
      {
        path: "/schedule",
        icon: <GetImages name="ScheduleNavIcon" width="36" height="36" />,
        title: intl.formatMessage({
          id: "schedule",
          defaultMessage: "Schedule",
        }),
        locationIcon: false,
        subText: "",
        secondIcon: (
          <Grid
            item
            className={MobileTopNavstyle.scheduleTopIconContainer}
            ml={2}
          >
            <Grid
              item
              onClick={() =>
                dispatch(
                  setTopBarDropdownItems(
                    topBarDropdownDispatchingEnums[
                      IProfileDropdownEnums.AVAILABILITY_SETTINGS
                    ]
                  )
                )
              }
            >
              <GetImages name="SettingGreyedIcon" width="24" height="24" />
            </Grid>
          </Grid>
        ),
        isShowSearch: true,
      },
      {
        path: "/ip-dash",
        icon: <GetImages name="IpDashNavIcon" width="36" height="36" />,
        title: selectedHospital,
        locationIcon: false,
        subTextHandler: () => {
          dispatch(
            setChangeHospitalModuleName(IChangeHospitalModuleName.FROM_LIST)
          );
          dispatch(
            setTopBarDropdownItems(
              topBarDropdownDispatchingEnums[
                IProfileDropdownEnums.CHANGE_HOSPITAL
              ]
            )
          );
        },
        subText: "Change",
        secondIcon: null,
        isShowSearch: true,
      },
      {
        path: "/chats",
        icon: <GetImages name="ChatNavIcon" width="36" height="36" />,
        title: intl.formatMessage({
          id: "chats",
          defaultMessage: "Chats",
        }),
        locationIcon: false,
        subText: "",
        secondIcon: (
          <>
            <Grid item onClick={() => dispatch(setChatSearchOn(true))}>
              <GetImages name="NavSearchIcon" width="24" height="24" />
            </Grid>
          </>
        ),
        isShowSearch: false,
      },
    ];

    const pharmacyItem = [
      {
        path: "/orders",
        icon: (
          <Grid item className={MobileTopNavstyle.pharmacyOrders}>
            <GetImages name="PharamcyOrdersIcon" width="55" height="16" />
            <Grid item className={MobileTopNavstyle.verticalLine}>
              <GetImages name="VerticalLineImage" width="1" height="22" />
            </Grid>
            <Grid
              item
              className={MobileTopNavstyle.request}
              onClick={() => dispatch(setIsPendingOrderRequestsOpen())}
            >
              <GetImages
                name={
                  pendingOrderList?.length > 0
                    ? "RequestNotificationDotIcon"
                    : "EyeOrangeIcon"
                }
                width="28"
                height="24"
              />
              <Typography component={"p"}>Request</Typography>
            </Grid>
          </Grid>
        ),
        title: "",
        locationIcon: false,
        subText: "",
        secondIcon: (
          <Grid item className={MobileTopNavstyle.scheduleTopIconContainer}>
            {!isClinicRole && (
              <Grid item onClick={() => navigate("/profile-details")} ml={2}>
                <GetImages name="ProfileSmileIcon" width="24" height="24" />
              </Grid>
            )}
          </Grid>
        ),
        isShowSearch: true,
      },
      {
        path: "/clinic-patient",
        icon: <GetImages name="PatientsModuleIcon" width="95" height="14" />,
        title: "",
        locationIcon: false,
        subText: "",
        secondIcon: null,
        isShowSearch: true,
      },
    ];
    return {
      [IRoleType.PATIENT]: patientOrGuestItem,
      [IRoleType.GUEST]: patientOrGuestItem,
      [IRoleType.DOCTOR]: doctorItem,
      [IRoleType.JR_DOCTOR]: doctorItem,
      [IRoleType.PHARMACY]: pharmacyItem,
      [IRoleType.DIAGNOSTIC_CENTER]: pharmacyItem,
    };
  };
  return { generateNavItems };
};

export default useMobileTopNavigation;
